// src/services/notificationService.js

import {
  collection, addDoc, updateDoc, doc, getDoc,
  getDocs, query, where, serverTimestamp, arrayUnion,
} from "firebase/firestore";
import { db } from "../firebase";

// ─────────────────────────────────────────────
// BASE
// ─────────────────────────────────────────────
export const createNotification = async ({
  type, title, body, route,
  for_roles = [], for_uid = "",
  reference_id = "", reference_type = "",
  priority = "medium", notification_class = "action_required",
  company_id, metadata = {},
}) => {
  if (!company_id) {
    console.warn("createNotification: company_id missing — skipping");
    return null;
  }
  try {
    const ref = await addDoc(collection(db, "notifications"), {
      type, title, body,
      route:              route || "",
      for_roles,
      for_uid:            for_uid || "",
      is_read:            false,
      resolved:           false,
      reference_id:       reference_id || "",
      reference_type:     reference_type || "",
      priority,
      notification_class, // "action_required" | "informational"
      company_id,
      metadata,
      created_at:         serverTimestamp(),
    });
    return { success: true, id: ref.id };
  } catch (err) {
    console.error("createNotification error:", err);
    return null;
  }
};

// ─────────────────────────────────────────────
// MARK READ
// ─────────────────────────────────────────────
export const markNotificationRead = async (notificationId, company_id) => {
  try {
    if (!company_id) throw new Error("company_id is required");
    const ref  = doc(db, "notifications", notificationId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error("Notification not found");
    if (snap.data().company_id !== company_id) {
      throw new Error("Access denied — notification belongs to a different company");
    }
    await updateDoc(ref, {
      is_read: true, read_at: serverTimestamp(),
    });
    return { success: true };
  } catch (err) {
    console.error("markNotificationRead error:", err);
    return null;
  }
};

export const markNotificationResolved = async (notificationId, company_id) => {
  try {
    if (!company_id) throw new Error("company_id is required");
    const ref  = doc(db, "notifications", notificationId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error("Notification not found");
    if (snap.data().company_id !== company_id) {
      throw new Error("Access denied — notification belongs to a different company");
    }
    await updateDoc(ref, {
      resolved: true, resolved_at: serverTimestamp(), is_read: true,
    });
    return { success: true };
  } catch (err) {
    console.error("markNotificationResolved error:", err);
    return null;
  }
};

export const markNotificationReadByReference = async (referenceId, type, companyId) => {
  if (!referenceId || !type || !companyId) return;
  try {
    const q = query(
      collection(db, "notifications"),
      where("company_id",   "==", companyId),
      where("reference_id", "==", referenceId),
      where("type",         "==", type),
      where("is_read",      "==", false)
    );
    const snap = await getDocs(q);
    await Promise.all(
      snap.docs.map(d => updateDoc(doc(db, "notifications", d.id), {
        is_read: true, resolved: true, read_at: serverTimestamp(),
      }))
    );
  } catch (err) {
    console.warn("[markNotificationReadByReference] non-blocking:", err);
  }
};



// ─────────────────────────────────────────────
// SERVICE MODULE
// ─────────────────────────────────────────────
export const notifyServiceAssigned = async ({
  service_id, tech_uid, tech_name, client_name, site_name, complaint, company_id,
}) => createNotification({
  type: "service_assigned", notification_class: "action_required",
  title: `🔧 New Task Assigned — ${client_name}`,
  body:  `${complaint || "Service task"} at ${site_name || ""}`,
  route: `/services/${service_id}`,
  for_roles: ["technician"], for_uid: tech_uid,
  reference_id: service_id, reference_type: "service_task",
  priority: "high", company_id,
});

export const notifyServiceCompletedPendingApproval = async ({
  service_id, tech_name, client_name, site_name, company_id,
}) => createNotification({
  type: "service_approval_pending", notification_class: "action_required",
  title: `✅ Service Completed — Approval Needed`,
  body:  `${tech_name} completed job at ${client_name} — ${site_name || ""}`,
  route: `/services/${service_id}`,
  for_roles: ["sm", "manager", "owner"],
  reference_id: service_id, reference_type: "service_task",
  priority: "high", company_id,
});

export const notifyServiceClosed = async ({
  service_id, client_name, site_name, company_id,
}) => createNotification({
  type: "service_closed", notification_class: "informational",
  title: `🎯 Service Closed — ${client_name}`,
  body:  `Job closed at ${site_name || client_name}`,
  route: `/services/${service_id}`,
  for_roles: ["owner", "sm"],
  reference_id: service_id, reference_type: "service_task",
  priority: "low", company_id,
});

// ─────────────────────────────────────────────
// BILLING MODULE
// ─────────────────────────────────────────────
export const notifyBillingPending = async ({
  service_id, client_name, site_name, chargeable_amount, amount_pending, company_id,
}) => {
  const title = amount_pending
    ? `💰 Billing Pending — ${client_name} (Amount not set)`
    : `🧾 Billing Pending — ₹${Number(chargeable_amount).toLocaleString("en-IN")} (${client_name})`;
  const body = amount_pending
    ? `Chargeable job closed. Amount not set. Site: ${site_name}`
    : `Ready to invoice ₹${Number(chargeable_amount).toLocaleString("en-IN")}. Site: ${site_name}`;
  return createNotification({
    type: "billing_pending", notification_class: "action_required",
    title, body,
    route: `/services/${service_id}`,
    for_roles: ["owner", "accounts"],
    reference_id: service_id, reference_type: "service_task",
    priority: amount_pending ? "high" : "medium", company_id,
  });
};

export const notifyInvoiceCreated = async ({
  invoice_id, invoice_number, client_name, amount, company_id,
}) => createNotification({
  type: "invoice_created", notification_class: "informational",
  title: `💵 Invoice Created — ₹${Number(amount).toLocaleString("en-IN")} (${client_name})`,
  body:  `Invoice ${invoice_number} created for ${client_name}`,
  route: `/billing/invoice/${invoice_id}`,
  for_roles: ["owner", "accounts"],
  reference_id: invoice_id, reference_type: "invoice",
  priority: "medium", company_id,
});

export const notifyInvoicePaid = async ({
  invoice_id, invoice_number, client_name, amount, company_id,
}) => createNotification({
  type: "invoice_paid", notification_class: "informational",
  title: `✅ Invoice Paid — ₹${Number(amount).toLocaleString("en-IN")} (${client_name})`,
  body:  `Invoice ${invoice_number} marked as paid`,
  route: `/billing/invoice/${invoice_id}`,
  for_roles: ["owner", "accounts"],
  reference_id: invoice_id, reference_type: "invoice",
  priority: "low", company_id,
});

// ─────────────────────────────────────────────
// MRF MODULE
// ─────────────────────────────────────────────
export const notifyMRFCreated = async ({
  mrf_id, requested_by_name, site_name, company_id,
}) => createNotification({
  type: "mrf_pending", notification_class: "action_required",
  title: `📋 New MRF — Approval Needed`,
  body:  `From ${requested_by_name}${site_name ? ` — ${site_name}` : ""}`,
  route: `/store-mrf`,
  for_roles: ["sm", "manager", "owner"],
  reference_id: mrf_id, reference_type: "material_request",
  priority: "medium", company_id,
});

export const notifyMRFIssued = async ({
  mrf_id, requested_by_name, site_name, company_id,
}) => createNotification({
  type: "mrf_issued", notification_class: "informational",
  title: `📦 MRF Issued`,
  body:  `Materials issued for ${requested_by_name}${site_name ? ` — ${site_name}` : ""}`,
  route: `/mrf-list`,
  for_roles: ["technician", "sm"],
  reference_id: mrf_id, reference_type: "material_request",
  priority: "low", company_id,
});

// ─────────────────────────────────────────────
// EXPENSE MODULE
// ─────────────────────────────────────────────
export const notifyExpenseSubmitted = async ({
  expense_id, submitted_by_name, amount, company_id,
}) => createNotification({
  type: "expense_pending", notification_class: "action_required",
  title: `💰 Expense Submitted — ₹${Number(amount).toLocaleString("en-IN")}`,
  body:  `${submitted_by_name} submitted an expense`,
  route: `/expense-approval`,
  for_roles: ["owner", "sm", "manager"],
  reference_id: expense_id, reference_type: "expense",
  priority: "medium", company_id,
});

export const notifyExpenseApproved = async ({
  expense_id, submitted_by_uid, amount, company_id,
}) => createNotification({
  type: "expense_approved", notification_class: "informational",
  title: `✅ Expense Approved — ₹${Number(amount).toLocaleString("en-IN")}`,
  body:  `Your expense has been approved`,
  route: `/expenses`,
  for_roles: ["technician"], for_uid: submitted_by_uid,
  reference_id: expense_id, reference_type: "expense",
  priority: "low", company_id,
});

// ─────────────────────────────────────────────
// PROCUREMENT MODULE
// ─────────────────────────────────────────────
export const notifyPOCreated = async ({
  po_id, po_number, vendor_name, total_amount, company_id,
}) => createNotification({
  type: "po_created", notification_class: "action_required",
  title: `🛒 PO Created — Approval Needed`,
  body:  `PO ${po_number} for ${vendor_name} — ₹${Number(total_amount).toLocaleString("en-IN")}`,
  route: `/purchase-orders/${po_id}`,
  for_roles: ["owner", "manager"],
  reference_id: po_id, reference_type: "purchase_order",
  priority: "medium", company_id,
});

export const notifyGRNCompleted = async ({
  po_id, po_number, vendor_name, company_id,
}) => createNotification({
  type: "grn_completed", notification_class: "informational",
  title: `📥 GRN Completed — ${vendor_name}`,
  body:  `PO ${po_number} received and stock updated`,
  route: `/purchase-orders/${po_id}`,
  for_roles: ["owner", "sm", "manager"],
  reference_id: po_id, reference_type: "purchase_order",
  priority: "low", company_id,
});

// ─────────────────────────────────────────────
// PROJECT MODULE
// ─────────────────────────────────────────────
export const notifyProjectMilestone = async ({
  project_id, project_name, milestone_label, company_id,
}) => createNotification({
  type: "project_milestone", notification_class: "informational",
  title: `🏆 Milestone Reached — ${project_name}`,
  body:  `${milestone_label} completed`,
  route: `/projects/${project_id}`,
  for_roles: ["owner", "sm"],
  reference_id: project_id, reference_type: "project",
  priority: "medium", company_id,
});

export const notifyProjectMRFPending = async ({
  mrf_id, project_name, stage_name, requested_by_name, company_id,
}) => createNotification({
  type: "project_mrf_pending", notification_class: "action_required",
  title: `📋 Project MRF — Approval Needed`,
  body:  `${project_name} — ${stage_name} — by ${requested_by_name}`,
  route: `/projects`,
  for_roles: ["sm", "manager", "owner"],
  reference_id: mrf_id, reference_type: "project_mrf",
  priority: "medium", company_id,
});

// ─────────────────────────────────────────────
// INVENTORY MODULE
// ─────────────────────────────────────────────
export const notifyLowStock = async ({
  item_id, item_name, current_stock, minimum_stock, company_id,
}) => createNotification({
  type: "low_stock", notification_class: "action_required",
  title: `⚠️ Low Stock — ${item_name}`,
  body:  `Stock: ${current_stock} (Min: ${minimum_stock}) — Reorder needed`,
  route: `/store-summary`,
  for_roles: ["sm", "manager", "owner"],
  reference_id: item_id, reference_type: "inventory_item",
  priority: "high", company_id,
});

export const notifyStockAdjustmentPending = async ({
  adjustment_id, adjustment_no, item_name, company_id,
}) => createNotification({
  type: "stock_adjustment_pending", notification_class: "action_required",
  title: `📊 Stock Adjustment — Approval Needed`,
  body:  `${adjustment_no} — ${item_name}`,
  route: `/stock-adjustment-approval`,
  for_roles: ["sm", "manager", "owner"],
  reference_id: adjustment_id, reference_type: "stock_adjustment",
  priority: "medium", company_id,
});

// ─────────────────────────────────────────────
// CRM MODULE
// ─────────────────────────────────────────────
export const notifyEnquiryFollowUp = async ({
  enquiry_id, enquiry_no, client_name, follow_up_date, company_id,
}) => createNotification({
  type: "enquiry_followup", notification_class: "action_required",
  title: `⏰ Follow-up Due — ${client_name}`,
  body:  `${enquiry_no} — Follow-up scheduled for ${follow_up_date}`,
  route: `/enquiries/${enquiry_id}`,
  for_roles: ["sm", "manager", "owner"],
  reference_id: enquiry_id, reference_type: "enquiry",
  priority: "high", company_id,
});

export const notifyEnquiryConverted = async ({
  enquiry_id, enquiry_no, client_name, converted_to, company_id,
}) => createNotification({
  type: "enquiry_converted", notification_class: "informational",
  title: `✅ Enquiry Converted — ${client_name}`,
  body:  `${enquiry_no} converted to ${converted_to}`,
  route: `/enquiries/${enquiry_id}`,
  for_roles: ["owner"],
  reference_id: enquiry_id, reference_type: "enquiry",
  priority: "low", company_id,
});

// ─────────────────────────────────────────────
// CASH COLLECTED
// ─────────────────────────────────────────────
export const notifyCashCollected = async ({
  service_id, tech_name, client_name, site_name, amount, mode, company_id,
}) => createNotification({
  type: "cash_collected", notification_class: "action_required",
  title: `💵 Cash Collected — ₹${Number(amount).toLocaleString("en-IN")} (${client_name})`,
  body:  `${tech_name} collected via ${(mode || "cash").toUpperCase()} from ${client_name} — ${site_name}`,
  route: `/services/${service_id}`,
  for_roles: ["owner", "accounts", "sm", "manager"],
  reference_id: service_id, reference_type: "service_task",
  priority: "high", company_id,
});
// ─────────────────────────────────────────────
// PROJECT MATERIAL STAGE
// ─────────────────────────────────────────────
export const notifyMaterialStageUnlocked = async ({
  stage_id, project_id, project_name, stage_name, trigger_item_name, company_id,
}) => createNotification({
  type: "material_stage_unlocked", notification_class: "action_required",
  title: `🔓 Material Stage Unlocked — ${project_name}`,
  body:  `"${stage_name}" is now unlocked${trigger_item_name ? ` (triggered by "${trigger_item_name}")` : ""}. Material can now be issued for this stage.`,
  route: `/projects/${project_id}/materials`,
  for_roles: ["store", "sm", "manager", "owner"],
  reference_id: stage_id, reference_type: "material_stage",
  priority: "medium", company_id,
});

// ─────────────────────────────────────────────
// STAGE VARIATION — Task B (budget-exceed recorded at issue-time). This is
// deliberately informational, not action_required — by the time this fires,
// the variation has already been recorded and the stage budget already
// adjusted (there's no live in-app approval-request flow, only after-the-
// fact recording of who approved it and how) — Owner/Manager are being
// told, not asked.
// ─────────────────────────────────────────────
export const notifyVariationRecorded = async ({
  project_id, project_name, stage_name, item_name, exceeded_qty, unit,
  extra_cost, approved_by_name, recorded_by_name, company_id,
}) => createNotification({
  type: "stage_variation_recorded", notification_class: "informational",
  title: `⚠️ Budget Variation — ${project_name}`,
  body:  `"${item_name}" in "${stage_name}" exceeded budget by ${exceeded_qty} ${unit} (+₹${Number(extra_cost).toLocaleString("en-IN")}). Approved by ${approved_by_name || "—"}, recorded by ${recorded_by_name || "—"}.`,
  route: `/projects/${project_id}/materials`,
  for_roles: ["owner", "manager"],
  reference_id: project_id, reference_type: "material_stage_variation",
  priority: "medium", company_id,
});
// ─────────────────────────────────────────────
// SERVICE REMARK — new chat message notification
// Sent to whichever side (technician or manager) did NOT write the remark.
// for_uid is set specifically — this is a 1:1 message notification, not a
// role-broadcast — so only the actual recipient sees it.
// ─────────────────────────────────────────────
export const notifyNewRemark = async ({
  service_id, remark_text, by_name, for_uid, client_name, site_name, company_id,
}) => {
  if (!for_uid) return null; // no known recipient (e.g. job not yet assigned) — skip silently
  return createNotification({
    type: "new_remark", notification_class: "action_required",
    title: `💬 New Message — ${by_name || "Team"}`,
    body:  `${remark_text?.slice(0, 80) || ""}${client_name ? ` (${client_name}${site_name ? ` — ${site_name}` : ""})` : ""}`,
    route: `/services/${service_id}`,
    for_roles: ["technician", "sm", "manager", "owner"],
    for_uid,
    reference_id: service_id, reference_type: "service_task",
    priority: "medium", company_id,
  });
};

// ─────────────────────────────────────────────
// PER-USER DISMISSAL — for notifications shared across multiple for_roles
// ─────────────────────────────────────────────
export const dismissNotificationForUser = async (notificationId, uid, company_id) => {
  if (!notificationId || !uid) return;
  try {
    if (!company_id) throw new Error("company_id is required");
    const ref  = doc(db, "notifications", notificationId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error("Notification not found");
    if (snap.data().company_id !== company_id) {
      throw new Error("Access denied — notification belongs to a different company");
    }
    await updateDoc(ref, {
      dismissed_by: arrayUnion(uid),
    });
  } catch (err) {
    console.warn("[dismissNotificationForUser] non-blocking:", err);
  }
};